interface MyHealthSettingsProps {
  isEnabled: boolean;
  linkedPatientsCount: number;
  onToggle: (enabled: boolean) => void;
  onTestConnection: () => void;
}

export function MyHealthSettings({
  isEnabled,
  linkedPatientsCount,
  onToggle,
  onTestConnection,
}: MyHealthSettingsProps) {
  return (
    <div className="space-y-6">
      {/* Integration Status */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              MyHealth Samþætting
            </h3>
            <p className="text-sm text-gray-600">
              Tengist við myhealth.myx.is fyrir samþætta sjúkraskrá
            </p>
          </div>
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={(e) => onToggle(e.target.checked)}
              className="sr-only"
            />
            <div
              className={`relative w-12 h-6 rounded-full transition-colors ${
                isEnabled ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <div
                className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  isEnabled ? 'transform translate-x-6' : ''
                }`}
              />
            </div>
          </label>
        </div>

        {isEnabled && (
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
            <div>
              <div className="text-2xl font-bold text-gray-900">{linkedPatientsCount}</div>
              <div className="text-sm text-gray-600">Tengdir sjúklingar</div>
            </div>
            <div className="flex items-center">
              <button
                onClick={onTestConnection}
                className="px-4 py-2 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
              >
                Prófa tengingu
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sync Settings */}
      {isEnabled && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Samstillingarstillingar
          </h3>
          <div className="space-y-3">
            <label className="flex items-start">
              <input
                type="checkbox"
                defaultChecked={true}
                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <div className="ml-3">
                <div className="text-sm font-medium text-gray-900">
                  Samstilla tímabókanir
                </div>
                <div className="text-xs text-gray-500">
                  Senda tímabókanir sjálfkrafa í MyHealth dagatal sjúklings
                </div>
              </div>
            </label>

            <label className="flex items-start">
              <input
                type="checkbox"
                defaultChecked={true}
                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <div className="ml-3">
                <div className="text-sm font-medium text-gray-900">
                  Samstilla meðferðir
                </div>
                <div className="text-xs text-gray-500">
                  Senda meðferðargögn í tannlæknaskrá sjúklings
                </div>
              </div>
            </label>

            <label className="flex items-start">
              <input
                type="checkbox"
                defaultChecked={true}
                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <div className="ml-3">
                <div className="text-sm font-medium text-gray-900">
                  Samstilla reikninga
                </div>
                <div className="text-xs text-gray-500">
                  Senda reikninga í útgjaldaskrá sjúklings
                </div>
              </div>
            </label>
          </div>
        </div>
      )}

      {/* Consent Scopes */}
      {isEnabled && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Samþykkissvið
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Sjúklingar stjórna hvaða upplýsingar tannlæknir má sjá og senda í MyHealth:
          </p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center text-gray-700">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Skoða tannlæknasögu
            </div>
            <div className="flex items-center text-gray-700">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Bæta við meðferðum
            </div>
            <div className="flex items-center text-gray-700">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Skoða ofnæmi
            </div>
            <div className="flex items-center text-gray-700">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Samstilla tímabókanir
            </div>
            <div className="flex items-center text-gray-700">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Samstilla útgjöld
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
